/** Sensibull-style payoff for the strategy's open positional basket
 * (operator request 2026-07-26): green on-expiry line with shaded
 * profit/loss regions, blue "today" Black-Scholes curve when the broker
 * session is live, spot/breakeven markers, and a payoff table at round
 * spot targets. Renders nothing while the strategy is flat — the card
 * appears with the basket. */

import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { IndexLink } from "@/components/ui/ContractLink";
import { cn } from "@/lib/cn";
import { payoffApi, type PayoffView } from "@/lib/payoffApi";
import type { Strategy } from "@/types/strategy";
import { PayoffChart } from "./PayoffChart";
import { PnlTable } from "./PnlTable";
import { formatLevel, formatMoney, pnlTone } from "./payoffMath";

const POLL_MS = 60_000;
const BASKET_KEY = (id: string) => ["payoff-basket", id] as const;
const PAYOFF_KEY = (id: string) => ["payoff", id] as const;

type View = "graph" | "table";

export function PayoffCard({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement | null {
  const basket = useQuery({
    queryKey: [...BASKET_KEY(strategy.id), strategy.execution_mode],
    queryFn: () => payoffApi.openTradeId(strategy.id, strategy.execution_mode),
    refetchInterval: POLL_MS,
  });
  if (basket.data === undefined) return null; // loading / error — no card yet
  if (basket.data === null) return <FlatNote />;
  return <PayoffBody tradeId={basket.data} />;
}

/** Shown while the strategy is flat, so the operator knows the payoff
 * view exists before the first basket (feedback 2026-07-26: an
 * invisible card reads as a missing feature). */
function FlatNote(): ReactElement {
  return (
    <section className="card mb-3" data-testid="payoff-card-empty">
      <div className="flex flex-wrap items-center gap-2">
        <i className="ti ti-chart-area-line text-lg text-accent" />
        <h3>Payoff</h3>
        <span className="text-xs text-ink-muted">
          No open position — the payoff graph and table appear here as soon as a trade is on.
        </span>
      </div>
    </section>
  );
}

function PayoffBody({ tradeId }: Readonly<{ tradeId: string }>): ReactElement | null {
  const [view, setView] = useState<View>("graph");
  const payoff = useQuery({
    queryKey: PAYOFF_KEY(tradeId),
    queryFn: () => payoffApi.payoff(tradeId),
    refetchInterval: POLL_MS,
  });
  if (payoff.data === undefined) return null;
  const data = payoff.data;
  return (
    <section className="card mb-3" data-testid="payoff-card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <i className="ti ti-chart-area-line text-lg text-accent" />
          <h3>Payoff</h3>
          <span className="text-xs text-ink-muted">
            <IndexLink name={data.underlying} />
            {data.expiry !== null && ` · expiry ${expiryLabel(data.expiry)}`}
          </span>
        </div>
        <ViewToggle view={view} onChange={setView} />
      </div>
      <LegsLine data={data} />
      {view === "graph" ? <PayoffChart data={data} /> : <PnlTable data={data} />}
      <StatChips data={data} />
      {!data.connected && (
        <div className="mt-2 text-xs text-ink-muted">
          <i className="ti ti-plug-off mr-1" />
          Broker not connected — showing the on-expiry line only. The blue &quot;today&quot; curve
          needs live premiums.
        </div>
      )}
    </section>
  );
}

function ViewToggle({
  view,
  onChange,
}: Readonly<{ view: View; onChange: (v: View) => void }>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line bg-surface-1 p-0.5">
      {(["graph", "table"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={cn(
            "rounded-ctl px-2 py-1 text-xs font-medium transition-colors",
            v === view ? "bg-accent-bg text-accent" : "text-ink-secondary hover:text-ink",
          )}
          onClick={() => {
            onChange(v);
          }}
        >
          {v === "graph" ? "Payoff graph" : "P&L table"}
        </button>
      ))}
    </div>
  );
}

function LegsLine({ data }: Readonly<{ data: PayoffView }>): ReactElement {
  return (
    <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs text-ink-secondary">
      {data.legs.map((leg) => (
        <span key={leg.symbol}>
          <span className={leg.side === "SELL" ? "text-danger" : "text-success"}>{leg.side}</span>{" "}
          {leg.strike} {leg.optionType} @ {leg.entryFill.toFixed(2)} × {leg.quantity}
        </span>
      ))}
    </div>
  );
}

function StatChips({ data }: Readonly<{ data: PayoffView }>): ReactElement {
  const credit = -data.netEntryCost;
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-secondary">
      <Stat
        label="Max profit"
        value={data.maxProfitUnbounded ? "Unlimited" : formatMoney(data.maxProfit)}
        tone="text-success"
      />
      <Stat
        label="Max loss"
        value={data.maxLossUnbounded ? "Unlimited" : formatMoney(data.maxLoss)}
        tone="text-danger"
      />
      <Stat
        label={data.breakevens.length === 1 ? "Breakeven" : "Breakevens"}
        value={data.breakevens.map(formatLevel).join(" / ") || "—"}
      />
      <Stat
        label={credit >= 0 ? "Net credit" : "Net debit"}
        value={formatMoney(Math.abs(credit))}
      />
      {data.lastMtm !== null && (
        <Stat label="MTM" value={formatMoney(data.lastMtm)} tone={pnlTone(data.lastMtm)} />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: Readonly<{ label: string; value: string; tone?: string }>): ReactElement {
  return (
    <span>
      <span className="text-ink-muted">{label}:</span>{" "}
      <span className={cn("font-mono", tone)}>{value}</span>
    </span>
  );
}

function expiryLabel(iso: string): string {
  return new Date(`${iso}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    timeZone: "Asia/Kolkata",
  });
}
